import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { ContentAssetUpdateInput } from "@colab/shared";

// `ContentAssetUpdateInput` is z.infer of the output side of the update
// schema (every field's transform widens to `string | null`), which would
// require every field on every PATCH call. The wire contract is partial —
// the worker re-validates with the schema's input side, treating missing
// fields as `undefined` → no-op. Mirror that on the SPA: `Partial<>` lets
// callers send just the diff they care about.
export type UpdateAssetInput = {
  id: string;
  patch: Partial<ContentAssetUpdateInput>;
};

// PATCH /api/admin/content/assets/:id
//
// Replaces revalidatePath("/admin/content") + revalidatePath(`/admin/content/${id}`)
// from /app/admin/content/_actions.ts::updateContentAsset.
export function useUpdateAsset() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, UpdateAssetInput>({
    mutationFn: ({ id, patch }) =>
      apiFetch<{ ok: true }>(`/admin/content/assets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: (_data, { id, patch }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.content.list() });
      queryClient.invalidateQueries({ queryKey: queryKeys.content.detail(id) });
      // Folder title edits ripple to the Move dialog picker; cheapest
      // check is `title in patch` since callers always send a partial.
      if ("title" in patch) {
        queryClient.invalidateQueries({ queryKey: queryKeys.content.folders() });
      }
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update asset");
    },
  });
}
